import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ContractDesignerPanel from "@/components/laboral/ContractDesignerPanel";
import api, { detailFromAxiosError, formatApiError } from "@/lib/api";
import { isEmbeddedBrowserContext } from "@/lib/embeddedBrowserContext";
import { touchSharedSessionActivity } from "@/lib/sessionIdle";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, ExternalLink, FileText, Loader2, ScrollText, Upload } from "lucide-react";
import { toast } from "sonner";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const FILTER_ALL = "__all__";
const ACCEPT_PLANTILLA = ".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const PREVIEW_FILENAMES = {
  contrato: "Vista_Previa_Contrato_Estandar.pdf",
  renuncia: "Vista_Previa_Renuncia_Estandar.pdf",
};

async function blobLooksLikePdf(blob) {
  if (!blob || blob.size < 5) return false;
  try {
    const head = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
    return head[0] === 0x25 && head[1] === 0x50 && head[2] === 0x44 && head[3] === 0x46;
  } catch {
    return false;
  }
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function openBlobInNewTab(blob) {
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank", "noopener,noreferrer");
  if (!win) {
    URL.revokeObjectURL(url);
    return false;
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return true;
}

function PlantillaEmpresaBlock({
  tipo,
  titulo,
  descripcion,
  plantillaInfo,
  modeloActivo,
  uploadBusy,
  onSelectModelo,
  onTriggerUpload,
  onDownload,
}) {
  const tieneArchivo = Boolean(plantillaInfo?.tiene_archivo);
  const modeloEmpresaDisabled = !tieneArchivo;

  return (
    <div className="rounded-md border border-dashed border-slate-300 bg-white p-4 space-y-3">
      <div>
        <p className="text-xs font-medium m-0">{titulo}</p>
        <p className="text-[11px] text-muted-foreground m-0 mt-1">{descripcion}</p>
      </div>

      {tieneArchivo ? (
        <p className="text-xs text-emerald-800 m-0">
          Archivo cargado: <span className="font-medium">{plantillaInfo.filename}</span>
          {plantillaInfo.uploaded_at ? ` · ${plantillaInfo.uploaded_at.slice(0, 10)}` : ""}
          {!plantillaInfo.almacenado_en_disco ? (
            <span className="text-amber-700"> · guardado en la base de datos (configure BNT.TaxRepository para disco)</span>
          ) : null}
        </p>
      ) : (
        <p className="text-xs text-slate-600 m-0">Sin plantilla propia cargada. Suba un PDF o DOCX.</p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploadBusy}
          onClick={onTriggerUpload}
          data-testid={`btn-subir-plantilla-${tipo}`}
        >
          {uploadBusy ? (
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
          ) : (
            <Upload className="h-3.5 w-3.5 mr-1" />
          )}
          {tieneArchivo ? "Reemplazar archivo" : "Seleccionar archivo"}
        </Button>
        {tieneArchivo ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={uploadBusy}
            onClick={onDownload}
            data-testid={`btn-descargar-plantilla-${tipo}`}
          >
            <Download className="h-3.5 w-3.5 mr-1" />
            Descargar plantilla
          </Button>
        ) : null}
      </div>

      <div className="space-y-2 pt-1 border-t border-slate-100">
        <p className="text-[11px] font-medium text-slate-700 m-0">Modelo a usar al generar documentos</p>
        <RadioGroup
          value={modeloActivo || "sistema"}
          onValueChange={onSelectModelo}
          className="flex flex-col sm:flex-row gap-3 sm:gap-6"
          disabled={uploadBusy}
        >
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <RadioGroupItem value="sistema" id={`modelo-sistema-${tipo}`} />
            <span>Modelo del sistema</span>
          </label>
          <label
            className={`flex items-center gap-2 text-xs ${modeloEmpresaDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
          >
            <RadioGroupItem value="empresa" id={`modelo-empresa-${tipo}`} disabled={modeloEmpresaDisabled} />
            <span>Modelo de la empresa</span>
          </label>
        </RadioGroup>
      </div>
    </div>
  );
}

/**
 * Pestaña «Contratos prediseñados»: muestra el modelo estándar del sistema y plantillas de empresa.
 */
export default function PlantillasLaboralesPanel({ clientId, patronales = [] }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [patronId, setPatronId] = useState(FILTER_ALL);
  const [previewTipo, setPreviewTipo] = useState("contrato");
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [uploadBusyTipo, setUploadBusyTipo] = useState("");
  const pdfUrlRef = useRef(null);
  const pdfBlobRef = useRef(null);
  const contratoInputRef = useRef(null);
  const renunciaInputRef = useRef(null);
  const previewSectionRef = useRef(null);
  const embeddedPreview = useMemo(() => isEmbeddedBrowserContext(), []);

  const patronParam = patronId && patronId !== FILTER_ALL ? patronId : "";

  const revokePdfPreview = () => {
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = null;
    }
    pdfBlobRef.current = null;
    setPdfUrl("");
    setPdfBlob(null);
  };

  const loadConfig = useCallback(async () => {
    if (!clientId) {
      setConfig(null);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/laboral/catalogo-trabajadores/plantillas/config", {
        params: { client_id: clientId, registro_patronal_id: patronParam || undefined },
      });
      setConfig(data);
    } catch (e) {
      setConfig(null);
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cargar plantillas");
    } finally {
      setLoading(false);
    }
  }, [clientId, patronParam]);

  const loadPdfPreview = useCallback(async (tipo, { autoDownload = false, scrollToPreview = false } = {}) => {
    if (!clientId) return;
    setPreviewTipo(tipo);
    setPdfBusy(true);
    revokePdfPreview();
    try {
      const path =
        tipo === "renuncia"
          ? "/laboral/catalogo-trabajadores/plantillas/vista-previa/renuncia"
          : "/laboral/catalogo-trabajadores/plantillas/vista-previa/contrato";
      const response = await api.get(path, {
        params: { client_id: clientId, registro_patronal_id: patronParam || undefined },
        responseType: "blob",
        timeout: 120000,
      });
      let blob = response.data;
      if (!(blob instanceof Blob)) {
        blob = new Blob([blob], { type: "application/pdf" });
      }
      if (!(await blobLooksLikePdf(blob))) {
        const msg = await detailFromAxiosError({ response: { data: blob, status: response.status } });
        throw new Error(msg || "La respuesta del servidor no es un PDF válido");
      }
      touchSharedSessionActivity();
      const url = URL.createObjectURL(blob);
      pdfUrlRef.current = url;
      pdfBlobRef.current = blob;
      setPdfUrl(url);
      setPdfBlob(blob);
      // En Simple Browser / webview el <object> PDF a veces navega toda la app (parece logout).
      if (autoDownload || embeddedPreview) {
        triggerBlobDownload(blob, PREVIEW_FILENAMES[tipo] || "vista_previa.pdf");
        toast.success(embeddedPreview && !autoDownload
          ? "PDF listo: descargado (el visor del IDE no embebe PDFs con seguridad)."
          : "Descarga iniciada");
      } else if (scrollToPreview) {
        requestAnimationFrame(() => {
          previewSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
    } catch (e) {
      const status = e?.response?.status;
      const msg = e.message || (await detailFromAxiosError(e)) || "No se pudo generar la vista previa PDF";
      if (status === 401 || status === 403) {
        toast.error(msg || "No autorizado para generar la vista previa");
      } else {
        toast.error(msg);
      }
    } finally {
      setPdfBusy(false);
    }
  }, [clientId, patronParam, embeddedPreview]);

  const onDownloadPdf = () => {
    if (!pdfBlob) {
      toast.warning("Primero genere la vista previa del PDF");
      return;
    }
    triggerBlobDownload(pdfBlob, PREVIEW_FILENAMES[previewTipo] || "vista_previa.pdf");
    toast.success("Descarga iniciada");
  };

  const onOpenPdfTab = () => {
    if (!pdfBlob) {
      toast.warning("Primero genere la vista previa del PDF");
      return;
    }
    const ok = openBlobInNewTab(pdfBlob);
    if (!ok) {
      toast.warning("El navegador bloqueó la nueva pestaña. Use «Descargar PDF».");
    }
  };

  const onUploadPlantilla = async (tipo, file) => {
    if (!clientId || !file) return;
    const ext = (file.name || "").toLowerCase();
    if (!ext.endsWith(".pdf") && !ext.endsWith(".docx")) {
      toast.error("Solo se aceptan archivos PDF (.pdf) o Word (.docx)");
      return;
    }
    setUploadBusyTipo(tipo);
    try {
      const body = new FormData();
      body.append("client_id", clientId);
      body.append("tipo", tipo);
      body.append("archivo", file);
      const { data } = await api.post("/laboral/catalogo-trabajadores/plantillas/empresa/subir", body, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      if (data?.config) setConfig(data.config);
      else await loadConfig();
      toast.success(tipo === "renuncia" ? "Formato de renuncia cargado" : "Modelo de contrato cargado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo subir la plantilla");
    } finally {
      setUploadBusyTipo("");
    }
  };

  const onPlantillaFileSelected = (tipo, fileList) => {
    const file = fileList?.[0];
    if (file) onUploadPlantilla(tipo, file);
  };

  const triggerPlantillaUpload = (tipo) => {
    if (tipo === "renuncia") renunciaInputRef.current?.click();
    else contratoInputRef.current?.click();
  };

  const onDownloadPlantillaEmpresa = async (tipo) => {
    if (!clientId) return;
    setUploadBusyTipo(tipo);
    try {
      const response = await api.get("/laboral/catalogo-trabajadores/plantillas/empresa/descargar", {
        params: { client_id: clientId, tipo },
        responseType: "blob",
        timeout: 120000,
      });
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
      const info = tipo === "renuncia" ? config?.plantilla_empresa_renuncia : config?.plantilla_empresa_contrato;
      triggerBlobDownload(blob, info?.filename || `Plantilla_${tipo}.pdf`);
      toast.success("Descarga iniciada");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo descargar la plantilla");
    } finally {
      setUploadBusyTipo("");
    }
  };

  const onSelectModeloActivo = async (tipo, modelo) => {
    if (!clientId || modelo === (tipo === "renuncia" ? config?.modelo_activo_renuncia : config?.modelo_activo_contrato)) {
      return;
    }
    setUploadBusyTipo(tipo);
    try {
      const { data } = await api.put("/laboral/catalogo-trabajadores/plantillas/modelo-activo", {
        client_id: clientId,
        tipo,
        modelo,
      });
      setConfig(data);
      toast.success(modelo === "empresa" ? "Se usará el modelo de la empresa" : "Se usará el modelo del sistema");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cambiar el modelo activo");
      await loadConfig();
    } finally {
      setUploadBusyTipo("");
    }
  };

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (!clientId) {
      revokePdfPreview();
      return undefined;
    }
    // En preview embebido no auto-generar PDF: evita peticiones blob pesadas y el <object>
    // que en algunos webviews reemplaza la SPA (el usuario ve «me sacó del sistema»).
    if (!embeddedPreview) {
      loadPdfPreview("contrato");
    }
    return () => revokePdfPreview();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, patronParam, embeddedPreview]);

  const muestra = config?.datos_muestra || {};
  const patron = muestra.patron || {};
  const trabajador = muestra.trabajador || {};

  if (!clientId) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        Seleccione un cliente para ver los modelos prediseñados.
      </p>
    );
  }

  return (
    <div className="space-y-6" data-testid="plantillas-laborales-panel">
      {config?.instrucciones ? (
        <div className="rounded-md border border-sky-200 bg-sky-50/90 px-4 py-3 text-sm text-sky-950 space-y-1">
          <p className="font-semibold m-0">Aviso legal — contratos prediseñados</p>
          <p className="m-0 whitespace-pre-line leading-relaxed">{config.instrucciones}</p>
        </div>
      ) : null}

      <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="space-y-1 min-w-[240px]">
          <Label className="text-xs text-muted-foreground">Registro patronal para la muestra</Label>
          <Select value={patronId} onValueChange={setPatronId} disabled={loading}>
            <SelectTrigger data-testid="select-patron-plantillas">
              <SelectValue placeholder="Primer patrón del cliente" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FILTER_ALL}>Primer registro patronal</SelectItem>
              {patronales.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.registro_patronal} — {p.nombre_sucursal_obra || p.razon_social_imss || "Patrón"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-lg border bg-card p-4 md:p-6 space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium text-[#0F2D5C]">
            <FileText className="h-4 w-4" />
            Contrato individual — modelo del sistema
          </div>
          <p className="text-xs text-muted-foreground m-0">
            Así se verá el PDF que el sistema generará por trabajador, usando datos del patrón seleccionado
            y un trabajador de ejemplo (nombre, CURP, puesto, salario, etc.).
          </p>

          <div className="rounded-md border bg-slate-50 p-3 text-xs space-y-2">
            <p className="m-0 font-medium text-slate-700">Datos de muestra en la vista previa</p>
            <p className="m-0"><span className="text-slate-500">Patrón:</span> {patron.razon_social_fiscal || "—"}</p>
            <p className="m-0"><span className="text-slate-500">Tipo:</span> {patron.tipo_persona === "fisica" ? "Persona física" : "Persona moral"}</p>
            <p className="m-0"><span className="text-slate-500">Representante:</span> {patron.tipo_persona === "fisica" ? "No aplica (comparece por su propio derecho)" : (patron.representante_legal || "(espacio en blanco en PDF)")}</p>
            <p className="m-0"><span className="text-slate-500">RP IMSS:</span> {patron.registro_patronal || "—"}</p>
            <p className="m-0"><span className="text-slate-500">Trabajador ejemplo:</span> {trabajador.nombre_completo || "—"}</p>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-medium text-slate-700 m-0">Cláusulas incluidas</p>
            <ul className="m-0 pl-4 space-y-1 text-xs text-slate-600 list-disc">
              {(config?.contrato_clausulas || []).map((c) => (
                <li key={c.titulo}>
                  <span className="font-medium text-slate-800">{c.titulo}:</span> {c.texto}
                </li>
              ))}
            </ul>
          </div>

          <PlantillaEmpresaBlock
            tipo="contrato"
            titulo="Modelo de la empresa"
            descripcion="Suba su contrato DOCX o PDF para conservarlo y usarlo al generar documentos masivos."
            plantillaInfo={config?.plantilla_empresa_contrato}
            modeloActivo={config?.modelo_activo_contrato}
            uploadBusy={uploadBusyTipo === "contrato"}
            onSelectModelo={(modelo) => onSelectModeloActivo("contrato", modelo)}
            onTriggerUpload={() => triggerPlantillaUpload("contrato")}
            onDownload={() => onDownloadPlantillaEmpresa("contrato")}
          />

          <ContractDesignerPanel clientId={clientId} />

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant={previewTipo === "contrato" ? "default" : "outline"}
              className={previewTipo === "contrato" ? "bg-[#0F2D5C] hover:bg-[#0a2247]" : ""}
              disabled={pdfBusy}
              onClick={() => loadPdfPreview("contrato", { scrollToPreview: true })}
              data-testid="btn-vista-previa-contrato"
            >
              {pdfBusy && previewTipo === "contrato" ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileText className="h-4 w-4 mr-2" />
              )}
              Ver PDF del contrato
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={pdfBusy}
              onClick={() => loadPdfPreview("contrato", { autoDownload: true })}
              data-testid="btn-descargar-vista-previa-contrato"
            >
              <Download className="h-4 w-4 mr-2" />
              Descargar PDF
            </Button>
          </div>
        </section>

        <section className="rounded-lg border bg-card p-4 md:p-6 space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium text-[#0F2D5C]">
            <ScrollText className="h-4 w-4" />
            Renuncia voluntaria — modelo del sistema
          </div>
          <p className="text-xs text-muted-foreground m-0">
            {config?.renuncia_descripcion ||
              "Carta de renuncia voluntaria con datos del trabajador y fecha de separación solicitada."}
          </p>

          <PlantillaEmpresaBlock
            tipo="renuncia"
            titulo="Formato de renuncia de la empresa"
            descripcion="Cargue su propio formato de renuncia para generarlo desde Trabajadores registrados."
            plantillaInfo={config?.plantilla_empresa_renuncia}
            modeloActivo={config?.modelo_activo_renuncia}
            uploadBusy={uploadBusyTipo === "renuncia"}
            onSelectModelo={(modelo) => onSelectModeloActivo("renuncia", modelo)}
            onTriggerUpload={() => triggerPlantillaUpload("renuncia")}
            onDownload={() => onDownloadPlantillaEmpresa("renuncia")}
          />

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant={previewTipo === "renuncia" ? "default" : "outline"}
              className={previewTipo === "renuncia" ? "bg-[#0F2D5C] hover:bg-[#0a2247]" : ""}
              disabled={pdfBusy}
              onClick={() => loadPdfPreview("renuncia", { scrollToPreview: true })}
              data-testid="btn-vista-previa-renuncia"
            >
              {pdfBusy && previewTipo === "renuncia" ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <ScrollText className="h-4 w-4 mr-2" />
              )}
              Ver PDF de renuncia
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={pdfBusy}
              onClick={() => loadPdfPreview("renuncia", { autoDownload: true })}
              data-testid="btn-descargar-vista-previa-renuncia"
            >
              <Download className="h-4 w-4 mr-2" />
              Descargar PDF
            </Button>
          </div>
        </section>
      </div>

      {config?.aviso_legal ? (
        <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 m-0">
          {config.aviso_legal}
        </p>
      ) : null}

      <section ref={previewSectionRef} className="rounded-lg border bg-card overflow-hidden">
        <div className="px-4 py-2 border-b bg-slate-50 flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-medium text-slate-700">
            Vista previa PDF — {previewTipo === "renuncia" ? "Renuncia voluntaria" : "Contrato individual"}
            {loading ? " (actualizando datos…)" : ""}
          </span>
          {pdfBlob ? (
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={onDownloadPdf} disabled={pdfBusy}>
                <Download className="h-3.5 w-3.5 mr-1" />
                Descargar
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={onOpenPdfTab} disabled={pdfBusy}>
                <ExternalLink className="h-3.5 w-3.5 mr-1" />
                Abrir en pestaña
              </Button>
            </div>
          ) : null}
        </div>
        <div className="min-h-[520px] bg-slate-100 flex flex-col items-center justify-center">
          {pdfBusy ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-16">
              <Loader2 className="h-5 w-5 animate-spin" />
              Generando vista previa…
            </div>
          ) : pdfUrl && pdfBlob && !embeddedPreview ? (
            <>
              <object
                title={`Vista previa ${previewTipo}`}
                data={pdfUrl}
                type="application/pdf"
                className="w-full min-h-[520px] h-[70vh] bg-white"
                data-testid="object-vista-previa-plantilla"
              >
                <p className="text-sm text-muted-foreground p-8 text-center">
                  Su navegador no puede mostrar el PDF embebido.
                  Use <strong>Descargar</strong> o <strong>Abrir en pestaña</strong>.
                </p>
              </object>
              <p className="text-[11px] text-slate-500 px-4 py-2 m-0 text-center">
                Si el visor del IDE no muestra el PDF, descárguelo o ábralo en el navegador del sistema.
              </p>
            </>
          ) : pdfBlob && embeddedPreview ? (
            <div className="py-12 px-6 text-center space-y-3 max-w-lg" data-testid="plantillas-pdf-embedded-fallback">
              <p className="text-sm text-slate-700 m-0">
                PDF generado. En el visor integrado no se embebe el documento (evita salir de la sesión).
                Descárguelo o ábralo en el navegador del sistema.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                <Button type="button" size="sm" onClick={onDownloadPdf}>
                  <Download className="h-3.5 w-3.5 mr-1" />
                  Descargar PDF
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={onOpenPdfTab}>
                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                  Abrir en pestaña
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-16 px-6 text-center">
              {embeddedPreview
                ? "Pulse «Ver PDF» o «Descargar PDF» arriba para generar el contrato o la renuncia."
                : "No hay vista previa disponible. Pulse «Ver PDF» o «Descargar PDF» arriba."}
            </p>
          )}
        </div>
      </section>

      <input
        ref={contratoInputRef}
        type="file"
        accept={ACCEPT_PLANTILLA}
        className="hidden"
        onChange={(e) => {
          onPlantillaFileSelected("contrato", e.target.files);
          e.target.value = "";
        }}
      />
      <input
        ref={renunciaInputRef}
        type="file"
        accept={ACCEPT_PLANTILLA}
        className="hidden"
        onChange={(e) => {
          onPlantillaFileSelected("renuncia", e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}
